import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface Product {
  id: string;
  name: string;
  slug: string;
  category: string;
  description: string;
  short_description: string;
  price: number | null;
  image_url: string;
  is_featured: boolean;
  created_at: string;
}

const ProductsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tous les produits');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<{ name: string; count: number }[]>([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setProducts(data || []);
      
      // Calculer les catégories et leurs compteurs
      const categoryMap = new Map<string, number>();
      let totalCount = 0;
      
      (data || []).forEach((product) => {
        totalCount++;
        const count = categoryMap.get(product.category) || 0;
        categoryMap.set(product.category, count + 1);
      });
      
      const categoriesList = [
        { name: 'Tous les produits', count: totalCount },
        ...Array.from(categoryMap.entries()).map(([name, count]) => ({ name, count }))
      ];
      
      setCategories(categoriesList);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.short_description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'Tous les produits' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <>
      <header>
        <h1 className="sr-only">Produits médicaux ADF Biomédical - Dispositifs médicaux certifiés en Algérie</h1>
      </header>
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative bg-[#1A1A1A] pt-28 sm:pt-32 pb-16 sm:pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 md:mb-6">
                Nos Produits Médicaux
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-white/80 max-w-3xl mx-auto mb-8 md:mb-12 px-4">
                Découvrez notre gamme complète de dispositifs médicaux certifiés et conformes aux normes internationales
              </p>
              
              {/* Search Bar */}
              <div className="max-w-2xl mx-auto px-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Rechercher un produit…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-base text-[#1A1A1A] bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-[#E31B23]"
                  />
                  <i className="ri-search-line absolute right-6 sm:right-8 top-1/2 transform -translate-y-1/2 text-xl sm:text-2xl text-[#666666]"></i>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Filters Section */}
        {categories.length > 0 && (
          <section className="bg-white py-6 sm:py-8 border-b border-gray-200 overflow-x-auto">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="flex flex-nowrap sm:flex-wrap gap-3 justify-start sm:justify-center pb-2 sm:pb-0"
              >
                {categories.map((category) => (
                  <button
                    key={category.name}
                    onClick={() => setSelectedCategory(category.name)}
                    className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                      selectedCategory === category.name
                        ? 'bg-[#E31B23] text-white shadow-md'
                        : 'bg-gray-100 text-[#1A1A1A] hover:bg-gray-200'
                    }`}
                  >
                    {category.name} ({category.count})
                  </button>
                ))}
              </motion.div>
            </div>
          </section>
        )}

        {/* Products Grid */}
        <section className="py-16 sm:py-20 bg-[#F8F8F8] min-h-[500px]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="text-center py-20">
                <i className="ri-loader-4-line animate-spin text-5xl text-[#E31B23] mb-4"></i>
                <p className="text-lg text-[#666666]">Chargement des produits...</p>
              </div>
            ) : filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-product-shop>
                {filteredProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group"
                  >
                    <div className="relative h-64 sm:h-72 overflow-hidden bg-gray-50">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-contain p-4 sm:p-6 transition-transform duration-500 group-hover:scale-105"
                      />
                      {product.is_featured && (
                        <div className="absolute top-4 right-4 px-3 py-1 bg-[#E31B23] text-white text-xs font-medium rounded-full">
                          En vedette
                        </div>
                      )}
                    </div>
                    <div className="p-5 sm:p-6">
                      <div className="mb-3">
                        <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                          {product.category}
                        </span>
                      </div>
                      <h3 className="text-lg sm:text-xl font-semibold text-[#1A1A1A] mb-2 sm:mb-3">
                        {product.name}
                      </h3>
                      <p className="text-xs sm:text-sm text-[#666666] mb-4 sm:mb-6 line-clamp-3 leading-relaxed">
                        {product.short_description || product.description}
                      </p>
                      <Link
                        to={`/products/${product.slug}`}
                        className="inline-flex items-center gap-2 text-sm sm:text-base text-[#E31B23] font-medium hover:gap-3 transition-all duration-300 cursor-pointer whitespace-nowrap"
                      >
                        Voir les détails
                        <i className="ri-arrow-right-line text-base sm:text-lg"></i>
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-box-3-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-2xl font-semibold text-[#1A1A1A] mb-2">Aucun produit trouvé</h3>
                <p className="text-base text-[#666666]">
                  {searchQuery ? 'Essayez une autre recherche' : 'Aucun produit disponible pour le moment'}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4 md:mb-6">
                Besoin d'informations sur nos produits ?
              </h3>
              <p className="text-base sm:text-lg text-[#666666] mb-6 md:mb-8 max-w-2xl mx-auto px-4">
                Notre équipe est à votre disposition pour vous conseiller et vous accompagner
              </p>
              <Link
                to="/contact"
                className="inline-block px-8 sm:px-10 py-3 sm:py-4 bg-[#E31B23] text-white rounded-lg font-medium text-base sm:text-lg hover:bg-[#C41820] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Nous contacter
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ProductsPage;
